/**
 * List of weighted items (landers OR offers) inside the right-pane detail.
 * Adds drag-handle on each row, "+ Lander/Offer" button, trash icon.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, ReactNode, useMemo } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import type { FlowLayer } from '@trafficpulse/shared';

import { Button } from '@/components/ui/layout/Button';
import { PATH_LIST_CONTAINER_CLASS } from '@/features/flow-optimizer/tabs/standard/v2/paths/path-list-chrome';
import {
  usePreviewMode,
  usePreviewRowLookup,
} from '@/features/flow-optimizer/components/preview/PreviewIndexContext';
import {
  PreviewSortLabel,
  usePreviewMetricSort,
} from '@/features/flow-optimizer/components/preview/sort';

import type { BinomDraftItem } from '../../binom-flow-draft.types';
import {
  dragEndToMove,
  useBinomDragSensors,
} from '../list/use-binom-drag-sensors';

import BinomDraggableItemRow from './BinomDraggableItemRow';

export interface BinomItemsListProps {
  kind: 'landers' | 'offers';
  pathId: string;
  items: BinomDraftItem[];
  onItemWeight: (idx: number, weight: number) => void;
  onItemActive: (idx: number, active: boolean) => void;
  onRemove: (idx: number) => void;
  onMove: (from: number, to: number) => void;
  onAdd?: () => void;
  onEditItem?: (idx: number) => void;
  extraAddButton?: ReactNode;
}

const BinomItemsList: FC<BinomItemsListProps> = ({
  kind,
  pathId,
  items,
  onItemWeight,
  onItemActive,
  onRemove,
  onMove,
  onAdd,
  onEditItem,
  extraAddButton,
}) => {
  const heading = kind === 'landers' ? 'Landers' : 'Offers';
  const previewMode = usePreviewMode();
  const lookup = usePreviewRowLookup();
  const { sort, cycle, sortEntries } = usePreviewMetricSort();
  const sensors = useBinomDragSensors();
  const ids = items.map((i) => i.id);
  const entries = useMemo(
    () =>
      sortEntries(
        items.map((item, index) => ({ index, item })),
        ({ item }) => ({
          active: item.active !== false,
          name: item.name ?? '',
          row: lookup(kind as FlowLayer, item.id, pathId),
        }),
      ),
    [items, kind, pathId, sortEntries, lookup],
  );

  const renderRow = (item: BinomDraftItem, idx: number) => (
    <BinomDraggableItemRow
      key={item.id}
      id={item.id}
      item={item}
      layer={kind}
      pathId={pathId}
      onWeightChange={(v) => onItemWeight(idx, v)}
      onActiveChange={(a) => onItemActive(idx, a)}
      onRemove={() => onRemove(idx)}
      onEdit={onEditItem ? () => onEditItem(idx) : undefined}
      dragDisabled={previewMode}
    />
  );
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="text-[10px] font-semibold uppercase tracking-wide text-foreground">
          {heading}
        </h4>
        <div className="flex items-center gap-2">
          <span className="text-[10px] tabular-nums text-muted-foreground">
            {items.length}
          </span>
          {onAdd && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 gap-1 px-1.5 text-[10px]"
              onClick={onAdd}
              aria-label={`Add ${kind === 'landers' ? 'lander' : 'offer'}`}
            >
              <Plus className="h-3 w-3" aria-hidden />{' '}
              {kind === 'landers' ? 'Lander' : 'Offer'}
            </Button>
          )}
          {extraAddButton}
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-[11px] italic text-muted-foreground">
          No {kind} configured — click "+" to attach.
        </p>
      ) : previewMode ? (
        <>
          <div className="flex items-center gap-2 px-1.5">
            <span className="w-3 shrink-0" aria-hidden />
            <span className="min-w-0 flex-1" aria-hidden />
            <div className="flex min-w-[12rem] items-center justify-end">
              <div className="grid grid-cols-[3rem_0.75rem_3rem_3.25rem] items-center gap-x-1.5">
                <PreviewSortLabel column="current" label="Current" sort={sort} onCycle={cycle} />
                <span />
                <PreviewSortLabel column="new" label="New" sort={sort} onCycle={cycle} />
                <PreviewSortLabel column="diff" label="Diff" sort={sort} onCycle={cycle} />
              </div>
            </div>
            <span className="w-[5.5rem] shrink-0" aria-hidden />
          </div>
          <div className={PATH_LIST_CONTAINER_CLASS} role="list" aria-label={heading}>
            {entries.map(({ item, index }) => renderRow(item, index))}
          </div>
        </>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(e) => {
            const m = dragEndToMove(e, ids);
            if (m) onMove(m.from, m.to);
          }}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <div className={PATH_LIST_CONTAINER_CLASS} role="list" aria-label={heading}>
              {items.map((item, idx) => renderRow(item, idx))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

export default BinomItemsList;
